"use client";

import type {
  ComponentPropsWithoutRef,
  CSSProperties,
  ReactNode,
} from "react";

import {
  buildNordicDigitRollSequence,
  CC_COUNT_UP_DURATION_MS,
  CC_COUNT_UP_EASING,
} from "@/lib/cc-count-up";
import {
  countCcDigitRollDigits,
  parseCcDigitRollText,
} from "@/lib/cc-digit-roll-text";
import {
  CC_DIGIT_ROLL_STAGGER_MS,
  useCcDigitRollInView,
  type UseCcDigitRollInViewOptions,
} from "@/lib/use-cc-digit-roll-in-view";

type CcDigitRollColumnProps = {
  target: number;
  showFinal: boolean;
  animate: boolean;
  staggerMs: number;
  accent?: boolean;
};

/** Single rolling digit column (Nordic odometer). */
export function CcDigitRollColumn({
  target,
  showFinal,
  animate,
  staggerMs,
  accent,
}: CcDigitRollColumnProps) {
  const sequence = buildNordicDigitRollSequence(target);
  const finalOffsetEm = sequence.length - 1;

  const groupClass = [
    "cc-digit-roll__group",
    accent ? "cc-digit-roll__group--accent" : "",
    showFinal ? "cc-digit-roll__group--final" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rollClass = [
    "cc-digit-roll__roll",
    animate ? "cc-digit-roll__roll--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={groupClass}
      style={
        {
          "--cc-digit-roll-duration": `${CC_COUNT_UP_DURATION_MS}ms`,
          "--cc-digit-roll-delay": `${staggerMs}ms`,
          "--cc-digit-roll-easing": CC_COUNT_UP_EASING,
          "--cc-digit-roll-offset-em": finalOffsetEm,
        } as CSSProperties
      }
    >
      <span className="cc-digit-roll__main" suppressHydrationWarning>
        {target}
      </span>
      <span className="cc-digit-roll__window" aria-hidden="true">
        <span className={rollClass}>
          {sequence.map((digit, index) => (
            <span
              key={`${digit}-${index}`}
              className={`cc-digit-roll__cell${index === sequence.length - 1 ? " cc-digit-roll__cell--final" : ""}`}
            >
              {digit}
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

type CcDigitRollTextProps = Omit<
  UseCcDigitRollInViewOptions,
  "digitCount"
> & {
  text: string;
  className?: string;
  /** Digit index (0-based among digits only) that gets accent styling. */
  accentDigitIndex?: number;
};

/** In-view digit roll for stat strings like `110+` or `10 000`. */
export function CcDigitRollText({
  text,
  className,
  accentDigitIndex,
  enabled,
  threshold,
  sessionStorageKey,
  digitStaggerMs = CC_DIGIT_ROLL_STAGGER_MS,
}: CcDigitRollTextProps) {
  const digitCount = countCcDigitRollDigits(text);
  const { rootRef, showFinal, animate } = useCcDigitRollInView({
    enabled,
    threshold,
    sessionStorageKey,
    digitCount,
    digitStaggerMs,
  });

  const segments = parseCcDigitRollText(text);

  return (
    <span
      ref={rootRef}
      className={["cc-digit-roll", className].filter(Boolean).join(" ")}
    >
      {segments.map((segment, index) =>
        segment.kind === "digit" ? (
          <CcDigitRollColumn
            key={`d-${segment.digitIndex}-${index}`}
            target={segment.value}
            accent={accentDigitIndex === segment.digitIndex}
            showFinal={showFinal}
            animate={animate}
            staggerMs={segment.digitIndex * digitStaggerMs}
          />
        ) : (
          <span key={`t-${index}`} className="cc-digit-roll__sep">
            {segment.value}
          </span>
        ),
      )}
    </span>
  );
}

type CcDigitRollDigitsProps = Omit<
  UseCcDigitRollInViewOptions,
  "digitCount"
> & {
  digits: readonly number[];
  className?: string;
  /** Which digit index receives accent styling (404 middle zero). */
  accentDigitIndex?: number;
  children?: ReactNode;
} & Pick<ComponentPropsWithoutRef<"span">, "aria-hidden" | "aria-label">;

/** Fixed digit array with shared in-view roll (404 code). */
export function CcDigitRollDigits({
  digits,
  className,
  accentDigitIndex,
  enabled,
  threshold,
  sessionStorageKey,
  digitStaggerMs = CC_DIGIT_ROLL_STAGGER_MS,
  children,
  ...spanProps
}: CcDigitRollDigitsProps) {
  const { rootRef, showFinal, animate } = useCcDigitRollInView({
    enabled,
    threshold,
    sessionStorageKey,
    digitCount: digits.length,
    digitStaggerMs,
  });

  return (
    <span
      ref={rootRef}
      className={["cc-digit-roll", className].filter(Boolean).join(" ")}
      {...spanProps}
    >
      {digits.map((target, index) => (
        <CcDigitRollColumn
          key={index}
          target={target}
          accent={accentDigitIndex === index}
          showFinal={showFinal}
          animate={animate}
          staggerMs={index * digitStaggerMs}
        />
      ))}
      {children}
    </span>
  );
}
